import { Button, Card, Chip, ModalBody, ModalContent, ModalFooter, ScrollShadow } from '@nextui-org/react'
import { FC, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import useHomeAssignGroupStore from '../../store/home/useHomeAssignGroupStore'
import { useGroupStore } from '@/store/group/useGroupStore'
import { BaseDivider, BaseModal, BaseModalHeader } from '../common/Modal'
import { useAssignGroup } from '../../hooks/useAssignGroup'
import { getGroups } from '@/api/group/group.service'
import { validateAssignment, formatValidationErrors } from '@/utils/assignGroupValidation'

export interface SelectGroupForAssignProps {
  isOpen: boolean
  onOpenChange: () => void
  isBack: () => void
}

export const SelectGroupForAssign: FC<SelectGroupForAssignProps> = ({
  isOpen,
  onOpenChange,
  isBack,
}) => {
  const {
    selectedTanks,
    selectedGroupId,
    selectedGroupName,
    setSelectedGroup,
    setGroupBorderColor,
    selectGroupBorderColor,
    validationErrors,
    setValidationErrors,
    clearValidationErrors,
    reset,
  } = useHomeAssignGroupStore()

  const groupsWithDetails = useGroupStore((state) => state.groupsWithDetails)
  const assignGroupMutation = useAssignGroup()

  // Fetch groups from /group API
  const { data: groups = [], isLoading: isLoadingGroups } = useQuery({
    queryKey: ['groups'],
    queryFn: getGroups,
    enabled: isOpen,
  })

  // Run validation when group is selected
  useEffect(() => {
    if (selectedGroupId && selectedTanks.length > 0) {
      const result = validateAssignment(selectedTanks, selectedGroupId, groupsWithDetails)
      setValidationErrors(result.errors)
    } else {
      clearValidationErrors()
    }
  }, [selectedGroupId, selectedTanks, groupsWithDetails, setValidationErrors, clearValidationErrors])

  // Check if assignment is valid
  const isValidAssignment = selectedGroupId !== null && validationErrors.length === 0

  // Transform groups to groupList format
  const groupList = groups.map((group) => ({
    id: group.id,
    name: group.name,
  }))

  const handleBack = (onClose: () => void) => {
    onClose()
    setTimeout(() => {
      isBack()
    }, 50)
  }

  const handleAssign = async (onClose: () => void) => {
    setGroupBorderColor()

    // Final validation check before assignment
    if (!isValidAssignment) {
      return
    }

    if (selectedGroupId && selectedTanks.length > 0) {
      try {
        await assignGroupMutation.mutateAsync({
          groupId: selectedGroupId,
          tankIds: selectedTanks.map((tank) => tank.tankId),
        })
        onClose()
        reset()
      } catch (error) {
        console.error('Failed to assign tanks to group:', error)
      }
    }
  }

  const handleGroupSelect = (groupId: string, groupName: string) => {
    setSelectedGroup(groupId, groupName)
  }

  return (
    <BaseModal isOpen={isOpen} onOpenChange={onOpenChange} size={'3xl'} isDismissable={true}>
      <ModalContent className="overflow-x-hidden">
        {(onClose) => (
          <>
            <BaseModalHeader
              title="Select Group"
              description={`Assign ${selectedTanks.length} tank${selectedTanks.length > 1 ? 's' : ''} to a batch group`}
            />
            <ModalBody className="gap-1 min-h-[8rem] pl-6 sm:pl-10 pr-6 sm:pr-10 overflow-x-hidden overflow-y-auto">
              <BaseDivider />

              {/* Selected tanks preview */}
              <div className="mt-2 mb-4">
                <div className="text-sm font-semibold text-tdpldarkgrey mb-2">Selected Tanks:</div>
                <div className="flex flex-wrap gap-1">
                  {selectedTanks.map((tank, index) => (
                    <Chip
                      key={index}
                      size="sm"
                      radius="md"
                      classNames={{
                        base: 'border-small border-tdpldarkgrey bg-tdplsky border-opacity-60',
                        content: 'font-semibold text-xs',
                      }}
                    >
                      {tank.tankName}
                    </Chip>
                  ))}
                </div>
              </div>

              <BaseDivider />

              {/* Group selection */}
              <div className="mt-3 font-bold">Select Target Group</div>
              <Card
                className={`${selectGroupBorderColor} border-opacity-60 p-0`}
                radius="md"
                shadow="sm"
              >
                <ScrollShadow className="max-h-[300px]">
                  <div className="flex flex-col gap-2 p-3">
                    {isLoadingGroups ? (
                      <div className="text-center text-gray-500 py-4">
                        Loading groups...
                      </div>
                    ) : groupList.length === 0 ? (
                      <div className="text-center text-gray-500 py-4">
                        No groups available. Please create a group first.
                      </div>
                    ) : (
                      groupList.map((group) => (
                        <Card
                          key={group.id}
                          isPressable
                          onPress={() => handleGroupSelect(group.id, group.name)}
                          className={`p-4 transition-all duration-200 ${selectedGroupId === group.id
                            ? 'bg-tdplsky border-2 border-tdplprussian shadow-md'
                            : 'bg-white border border-tdpldarkgrey border-opacity-40 hover:bg-tdpllightgrey hover:border-opacity-70'
                            }`}
                          radius="md"
                          shadow="none"
                        >
                          <span
                            className={`font-semibold ${selectedGroupId === group.id
                              ? 'text-tdplprussian'
                              : 'text-tdpldarkgrey'
                              }`}
                          >
                            {group.name}
                          </span>
                        </Card>
                      ))
                    )}
                  </div>
                </ScrollShadow>
              </Card>

              {selectGroupBorderColor.includes('tdplmaroon') && (
                <p className="m-0 text-sm font-semibold text-tdplmaroon">
                  Please select a target group.
                </p>
              )}

              {/* Validation Errors Display */}
              {validationErrors.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="font-semibold text-red-700 mb-2">
                    Validation Errors ({validationErrors.length})
                  </div>
                  <ul className="list-disc list-inside text-sm text-red-600 space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>
                        {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </ModalBody>

            <ModalFooter className="pb-6 sm:pb-8 pl-6 sm:pl-10 pr-6 sm:pr-10">
              <div className="flex gap-3 justify-end w-full">
                <Button
                  variant="light"
                  className="font-bold text-tdplindigo border border-tdplindigo"
                  onPress={() => handleBack(onClose)}
                >
                  Back
                </Button>
                <Button
                  className={`font-bold text-white w-[6rem] mr-0 ${
                    !isValidAssignment ? 'bg-tdplpigpink' : 'bg-tdplplum'
                  }`}
                  onPress={() => handleAssign(onClose)}
                  isLoading={assignGroupMutation.isPending}
                  isDisabled={groupList.length === 0 || isLoadingGroups || !isValidAssignment}
                >
                  {assignGroupMutation.isPending ? 'Assigning...' : 'Assign'}
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </BaseModal>
  )
}
